import { useState, useCallback } from "react";
import { useTaskFiles, uploadFileDirect, downloadFile as downloadFileAPI, deleteFile as deleteFileAPI, } from "../api/files";
import { FileValidationService } from "../services/fileService";
/**
 * Hook for file upload operations
 */
export function useFileUpload(workspaceId, taskId) {
    const [uploadProgress, setUploadProgress] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const uploadFiles = useCallback(async (files) => {
        const fileArray = Array.from(files);
        const results = [];
        setIsUploading(true);
        try {
            for (const file of fileArray) {
                const fileId = crypto.randomUUID();
                // Initialize progress tracking
                setUploadProgress((prev) => ({
                    ...prev,
                    [fileId]: {
                        fileId,
                        filename: file.name,
                        progress: 0,
                        status: "pending",
                    },
                }));
                // Validate file
                const validation = FileValidationService.validateFile(file);
                if (!validation.isValid) {
                    const error = validation.errors.join(", ");
                    setUploadProgress((prev) => ({
                        ...prev,
                        [fileId]: { ...prev[fileId], status: "error", error },
                    }));
                    results.push({ success: false, error });
                    continue;
                }
                try {
                    // Update status to uploading
                    setUploadProgress((prev) => ({
                        ...prev,
                        [fileId]: { ...prev[fileId], status: "uploading" },
                    }));
                    // Upload file directly
                    const uploadedFile = await uploadFileDirect(workspaceId, file, taskId, fileId, (progress) => {
                        setUploadProgress((prev) => ({
                            ...prev,
                            [fileId]: { ...prev[fileId], progress },
                        }));
                    });
                    // Update status to completed
                    setUploadProgress((prev) => ({
                        ...prev,
                        [fileId]: { ...prev[fileId], status: "completed", progress: 100 },
                    }));
                    results.push({ success: true, file: uploadedFile });
                }
                catch (error) {
                    const errorMessage = error instanceof Error ? error.message : "Upload failed";
                    setUploadProgress((prev) => ({
                        ...prev,
                        [fileId]: {
                            ...prev[fileId],
                            status: "error",
                            error: errorMessage,
                        },
                    }));
                    results.push({ success: false, error: errorMessage });
                }
            }
            // Note: File list will be revalidated by the parent component
            return results;
        }
        finally {
            setIsUploading(false);
            // Clear progress after a delay
            setTimeout(() => {
                setUploadProgress({});
            }, 3000);
        }
    }, [taskId]);
    const clearProgress = useCallback(() => {
        setUploadProgress({});
    }, []);
    return {
        uploadFiles,
        uploadProgress,
        isUploading,
        clearProgress,
    };
}
/**
 * Hook for file download operations
 */
export function useFileDownload(workspaceId) {
    const [isDownloading, setIsDownloading] = useState({});
    const downloadFile = useCallback(async (fileId) => {
        setIsDownloading((prev) => ({ ...prev, [fileId]: true }));
        try {
            await downloadFileAPI(workspaceId, fileId);
            return { success: true, message: "Download started" };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Download failed";
            return { success: false, error: errorMessage };
        }
        finally {
            setIsDownloading((prev) => ({ ...prev, [fileId]: false }));
        }
    }, []);
    return {
        downloadFile,
        isDownloading,
    };
}
/**
 * Hook for file deletion operations
 */
export function useFileDelete(workspaceId, taskId) {
    const [isDeleting, setIsDeleting] = useState({});
    const deleteFile = useCallback(async (fileId) => {
        setIsDeleting((prev) => ({ ...prev, [fileId]: true }));
        try {
            await deleteFileAPI(workspaceId, fileId);
            // Note: File list will be revalidated by the parent component
            return { success: true, message: "File deleted successfully" };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Delete failed";
            return { success: false, error: errorMessage };
        }
        finally {
            setIsDeleting((prev) => ({ ...prev, [fileId]: false }));
        }
    }, [taskId]);
    return {
        deleteFile,
        isDeleting,
    };
}
/**
 * Hook for comprehensive file management
 */
export function useFileManager(workspaceId, taskId) {
    const { data: files, error, isLoading, refetch: revalidateFiles } = useTaskFiles(workspaceId, taskId);
    const { uploadFiles, uploadProgress, isUploading, clearProgress } = useFileUpload(workspaceId, taskId);
    const { downloadFile, isDownloading } = useFileDownload(workspaceId);
    const { deleteFile, isDeleting } = useFileDelete(workspaceId, taskId);
    const [selectedFiles, setSelectedFiles] = useState(new Set());
    const selectFile = useCallback((fileId) => {
        setSelectedFiles((prev) => new Set([...prev, fileId]));
    }, []);
    const deselectFile = useCallback((fileId) => {
        setSelectedFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
        });
    }, []);
    const toggleFileSelection = useCallback((fileId) => {
        setSelectedFiles((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) {
                newSet.delete(fileId);
            }
            else {
                newSet.add(fileId);
            }
            return newSet;
        });
    }, []);
    const clearSelection = useCallback(() => {
        setSelectedFiles(new Set());
    }, []);
    const deleteSelectedFiles = useCallback(async () => {
        const results = [];
        for (const fileId of selectedFiles) {
            const result = await deleteFile(fileId);
            results.push(result);
        }
        clearSelection();
        return results;
    }, [selectedFiles, deleteFile, clearSelection]);
    return {
        // Data
        files: files || [],
        error,
        isLoading,
        // Operations
        uploadFiles,
        downloadFile,
        deleteFile,
        deleteSelectedFiles,
        revalidateFiles,
        // State
        uploadProgress,
        isUploading,
        isDownloading,
        isDeleting,
        // Selection
        selectedFiles,
        selectFile,
        deselectFile,
        toggleFileSelection,
        clearSelection,
        // Utilities
        clearProgress,
    };
}
/**
 * Hook for file validation
 */
export function useFileValidation() {
    const validateFiles = useCallback((files) => {
        return Array.from(files).map((file) => FileValidationService.validateFile(file));
    }, []);
    const validateSingleFile = useCallback((file) => {
        return FileValidationService.validateFile(file);
    }, []);
    return {
        validateFiles,
        validateSingleFile,
    };
}
